import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Bell, BellOff, Loader, Gavel, ExternalLink, FolderOpen,
} from 'lucide-react';
import { bookmarkService } from '../../lib/bookmarkService';
import {
  docketFollowService,
  DigestFrequency,
  FollowedDocketWithMeta,
} from '../../lib/docketFollowService';
import { supabase } from '../../lib/supabase';
import { DigestPreviewDocket } from '../../lib/watchlistDigestService';
import { Bookmark } from '../../types';

// ── Helpers ───────────────────────────────────────────────────────────────────

const FREQ_LABELS: Record<DigestFrequency, string> = {
  immediate: 'Immediate',
  daily: 'Daily',
  weekly: 'Weekly',
  none: 'Alerts off',
};

// ── Types ─────────────────────────────────────────────────────────────────────

interface EnrichedDocket {
  bookmark: Bookmark;
  proceeding_name: string | null;
  status: string | null;
  followData: FollowedDocketWithMeta | null;
  newFilings: number;
}

interface LibraryDocketsTabProps {
  digestDockets: DigestPreviewDocket[];
}

// ── Component ─────────────────────────────────────────────────────────────────

export const LibraryDocketsTab: React.FC<LibraryDocketsTabProps> = ({ digestDockets }) => {
  const navigate = useNavigate();
  const [dockets, setDockets] = useState<EnrichedDocket[]>([]);
  const [loading, setLoading] = useState(true);

  // Build new-filings map from digest preview data (passed from parent,
  // so we don't re-fetch it here).
  const digestMap = new Map<string, number>(
    digestDockets
      .filter(d => d.new_filings_count > 0)
      .map(d => [d.docket_number, d.new_filings_count])
  );

  useEffect(() => {
    const load = async () => {
      try {
        const [bookmarks, follows] = await Promise.all([
          bookmarkService.getBookmarks(),
          docketFollowService.getFollowedDockets(),
        ]);

        if (bookmarks.length === 0) {
          setDockets([]);
          return;
        }

        // Fetch proceeding metadata for all bookmarked dockets in one query
        const docketNumbers = bookmarks.map(b => b.docket_number);
        const { data: procData } = await supabase
          .from('open_proceedings')
          .select('docket_number, proceeding_name, status')
          .in('docket_number', docketNumbers);

        type ProcRow = { docket_number: string; proceeding_name: string | null; status: string | null };
        const proceedings = (procData || []) as ProcRow[];
        const procMap = new Map(proceedings.map(p => [p.docket_number, p]));
        const followMap = new Map(follows.map(f => [f.docket_number, f]));

        setDockets(
          bookmarks.map(b => ({
            bookmark: b,
            proceeding_name: procMap.get(b.docket_number)?.proceeding_name ?? null,
            status: procMap.get(b.docket_number)?.status ?? null,
            followData: followMap.get(b.docket_number) ?? null,
            newFilings: digestMap.get(b.docket_number) ?? 0,
          }))
        );
      } catch (err) {
        console.error('[LibraryDocketsTab] load error:', err);
        setDockets([]);
      } finally {
        setLoading(false);
      }
    };

    load();
  // digestDockets changes when the parent re-fetches; rebuild the map accordingly.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(digestDockets.map(d => d.docket_number + d.new_filings_count))]);

  // ── Render ─────────────────────────────────────────────────────────────────

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader className="h-5 w-5 animate-spin text-gray-400" />
      </div>
    );
  }

  if (dockets.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-20 text-center">
        <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-gray-100 mb-4">
          <FolderOpen className="h-7 w-7 text-gray-400" />
        </div>
        <h3 className="text-base font-semibold text-gray-700 mb-1">No saved dockets yet</h3>
        <p className="text-sm text-gray-500 max-w-xs mb-5">
          Bookmark dockets from the Proceedings page to keep them here for quick access.
        </p>
        <button
          onClick={() => navigate('/proceedings')}
          className="rounded-lg bg-gray-900 px-4 py-2 text-sm font-medium text-white hover:bg-gray-800 transition-colors"
        >
          Browse proceedings
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {dockets.map(({ bookmark, proceeding_name, status, followData, newFilings }) => (
        <div
          key={bookmark.id}
          className="group flex items-start gap-4 rounded-xl border border-gray-200 bg-white p-4 transition-shadow hover:shadow-sm"
        >
          {/* Icon */}
          <div className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-gray-100 text-gray-500 group-hover:bg-gray-900 group-hover:text-white transition-colors">
            <Gavel className="h-4 w-4" />
          </div>

          {/* Body */}
          <div className="min-w-0 flex-1">
            <div className="flex items-start justify-between gap-3">

              {/* Left: docket title */}
              <div className="min-w-0">
                <button
                  onClick={() =>
                    navigate(`/proceedings/${encodeURIComponent(bookmark.docket_number)}`)
                  }
                  className="flex items-center gap-1.5 text-sm font-semibold text-gray-900 hover:text-gray-700 transition-colors text-left"
                >
                  {bookmark.docket_number}
                  <ExternalLink className="h-3 w-3 opacity-0 group-hover:opacity-40 transition-opacity" />
                </button>
                {proceeding_name && (
                  <p className="mt-0.5 text-xs text-gray-500 line-clamp-1">{proceeding_name}</p>
                )}
              </div>

              {/* Right: badges */}
              <div className="flex shrink-0 flex-wrap items-center gap-1.5">
                {/* New-filings badge from watchlist digest */}
                {newFilings > 0 && (
                  <span className="rounded-full bg-emerald-100 px-2.5 py-0.5 text-[11px] font-semibold text-emerald-700">
                    +{newFilings} new
                  </span>
                )}

                {/* Watchlist / alert-frequency badge */}
                {followData ? (
                  <span
                    className={`flex items-center gap-1 rounded-full border px-2.5 py-0.5 text-[11px] font-medium ${
                      followData.notify_new_filings
                        ? 'border-emerald-200 bg-emerald-50 text-emerald-700'
                        : 'border-neutral-200 bg-neutral-50 text-neutral-500'
                    }`}
                  >
                    {followData.notify_new_filings ? (
                      <Bell className="h-2.5 w-2.5" />
                    ) : (
                      <BellOff className="h-2.5 w-2.5" />
                    )}
                    {FREQ_LABELS[followData.digest_frequency as DigestFrequency]}
                  </span>
                ) : (
                  <button
                    onClick={() => navigate('/watchlist')}
                    className="flex items-center gap-1 rounded-full border border-dashed border-gray-300 px-2.5 py-0.5 text-[11px] font-medium text-gray-400 hover:border-gray-400 hover:text-gray-600 transition-colors"
                    title="Add to watchlist to get filing alerts"
                  >
                    <BellOff className="h-2.5 w-2.5" />
                    Not watching
                  </button>
                )}

                {/* Proceeding status */}
                {status && (
                  <span
                    className={`rounded-full px-2 py-0.5 text-[10px] font-medium ${
                      status === 'open'
                        ? 'bg-emerald-50 text-emerald-700'
                        : 'bg-neutral-100 text-neutral-500'
                    }`}
                  >
                    {status === 'open' ? 'Active' : status}
                  </span>
                )}
              </div>
            </div>

            {/* Meta row */}
            <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-[11px] text-gray-400">
              <span>
                Saved{' '}
                {new Date(bookmark.created_at).toLocaleDateString('en-US', {
                  month: 'short',
                  day: 'numeric',
                  year: 'numeric',
                })}
              </span>
              {followData ? (
                <button
                  onClick={() => navigate('/watchlist')}
                  className="hover:text-gray-600 transition-colors underline underline-offset-2"
                >
                  Manage alerts
                </button>
              ) : (
                <button
                  onClick={() => navigate('/watchlist')}
                  className="hover:text-gray-600 transition-colors underline underline-offset-2"
                >
                  + Add to watchlist
                </button>
              )}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};
